import React from "react";
import {
  Box,
  Button,
  Collapse,
  Container,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import logo from "../../../images/LogoNavbar1.svg";
import { Link as RouterLink } from "react-router-dom";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";

export const AUTH_BRAND = Object.freeze({
  orange: "#F35A3A",
  dark: "var(--wp-panel-text-primary, #19242F)",
});

export const authPrimaryButtonSx = {
  minHeight: 48,
  bgcolor: AUTH_BRAND.orange,
  borderRadius: 999,
  textTransform: "none",
  fontWeight: 900,
  px: 2.5,
  "&:hover": { bgcolor: "#dc4d31" },
};

export const authSecondaryButtonSx = {
  minHeight: 44,
  borderRadius: 999,
  textTransform: "none",
  fontWeight: 850,
  color: AUTH_BRAND.dark,
};

export const authLinkSx = {
  color: AUTH_BRAND.orange,
  fontWeight: 900,
};

export function AuthPageShell({
  title,
  subtitle,
  icon,
  help,
  helpLabel = "Más información",
  children,
  footer,
}) {
  const [helpOpen, setHelpOpen] = React.useState(false);

  // UX-2026 | Shared entrance for login, sign-up and account recovery.
  // The two-column treatment is deliberately local to auth; the ERP theme and
  // all authenticated navigation remain untouched.
  return (
    <Box
      component="main"
      sx={{
        minHeight: "100dvh",
        display: "flex",
        alignItems: "center",
        bgcolor: "var(--wp-panel-surface-soft, #F7F8FB)",
        backgroundImage: "radial-gradient(circle at 7% 12%, rgba(243,90,58,.075), transparent 36%)",
        py: { xs: 2, md: 5 },
      }}
    >
      <Container maxWidth="md" sx={{ px: { xs: 1.5, sm: 2.5 } }}>
        <Box
          component={RouterLink}
          to="/"
          sx={{
            mb: 2, width: "fit-content", minHeight: 44,
            display: "inline-flex", alignItems: "center", gap: .75,
            color: "var(--wp-panel-text-primary, #19242F)",
            textDecoration: "none", fontWeight: 800, fontSize: ".85rem",
            "&:hover": { color: "#F35A3A" },
            "&:focus-visible": { outline: "3px solid #F35A3A", outlineOffset: 3 },
          }}
        >
          <ArrowBackRoundedIcon fontSize="small" /> Volver al inicio
        </Box>
        <Paper
          elevation={0}
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(0,.91fr) minmax(0,1fr)" },
            overflow: "hidden",
            borderRadius: { xs: 3.25, md: 4.5 },
            border: "1px solid var(--wp-panel-border, rgba(25,36,47,.09))",
            boxShadow: "0 26px 80px rgba(17,24,32,.1)",
            bgcolor: "var(--wp-panel-surface, #fff)",
          }}
        >
          <Box
            sx={{
              bgcolor: "#111A23", color: "#fff", position: "relative",
              background: "radial-gradient(circle at 15% 90%, rgba(243,90,58,.17), transparent 52%), #111A23",
              p: { xs: 2.25, sm: 3.5, md: 4.25 },
              display: "flex", flexDirection: "column", justifyContent: "space-between",
              minHeight: { xs: 134, md: 510 },
            }}
          >
            <Stack direction="row" spacing={1.15} alignItems="center">
              <Box component="img" src={logo} alt="" aria-hidden="true" sx={{ width: 40, height: 40, objectFit: "contain" }} />
              <Typography sx={{ fontWeight: 900, letterSpacing: "-.04em", fontSize: "1.28rem" }}>WebProfit</Typography>
            </Stack>
            <Box sx={{ display: { xs: "none", md: "block" } }}>
              <Typography component="p" sx={{ fontWeight: 900, lineHeight: 1.09, letterSpacing: "-.045em", fontSize: "2.3rem", mb: 2.1 }}>
                Tu negocio, más claro.
              </Typography>
              <Typography sx={{ color: "rgba(247,248,250,.72)", lineHeight: 1.7, mb: 2.8, fontSize: ".92rem" }}>
                Un espacio para tu página web, tus ventas y la gestión diaria.
              </Typography>
              {["Empezá con lo esencial", "Sumá herramientas cuando las necesites", "Todo desde un mismo panel"].map((value) => (
                <Stack key={value} direction="row" spacing={1.05} alignItems="center" sx={{ mb: 1.35 }}>
                  <CheckCircleOutlineRoundedIcon sx={{ fontSize: 19, color: "#FF9D84" }} />
                  <Typography sx={{ fontWeight: 650, fontSize: ".86rem" }}>{value}</Typography>
                </Stack>
              ))}
            </Box>
            <Typography sx={{ display: { xs: "block", md: "none" }, color: "rgba(255,255,255,.8)", mt: 1.2, fontSize: ".84rem" }}>
              Web, ventas y gestión en un solo lugar.
            </Typography>
            <Typography sx={{ display: { xs: "none", md: "block" }, color: "rgba(255,255,255,.48)", fontSize: ".73rem" }}>Un sistema que crece con vos.</Typography>
          </Box>

          <Box sx={{ p: { xs: 2.2, sm: 3.5, md: 4.2 }, minWidth: 0, alignSelf: "center" }}>
            <Stack spacing={2.15}>
              <Box>
                {icon ? <Box sx={{ mb: 1, color: "#F35A3A", "& svg": { fontSize: 26 } }}>{icon}</Box> : null}
                <Typography component="h1" sx={{ fontWeight: 900, letterSpacing: "-.04em", lineHeight: 1.15, color: AUTH_BRAND.dark, fontSize: { xs: "1.65rem", sm: "1.95rem" } }}>
                  {title}
                </Typography>
                {subtitle ? <Typography sx={{ color: "var(--wp-panel-text-secondary, #626B76)", mt: .8, fontSize: ".88rem", lineHeight: 1.55 }}>{subtitle}</Typography> : null}
              </Box>

              {children}

              {help ? (
                <Box>
                  <Button
                    type="button"
                    size="small"
                    startIcon={<HelpOutlineRoundedIcon fontSize="small" />}
                    endIcon={<KeyboardArrowDownRoundedIcon fontSize="small" sx={{ transform: helpOpen ? "rotate(180deg)" : "none", transition: "transform .18s ease" }} />}
                    onClick={() => setHelpOpen((value) => !value)}
                    aria-expanded={helpOpen}
                    sx={{ minHeight: 44, width: "100%", color: "var(--wp-panel-text-secondary, #59636D)", textTransform: "none", fontWeight: 750, fontSize: ".8rem" }}
                  >{helpLabel}</Button>
                  <Collapse in={helpOpen} unmountOnExit>
                    <Box sx={{ mt: .5, p: 1.4, bgcolor: "var(--wp-panel-surface-soft, #F7F8FB)", borderRadius: 2, color: "var(--wp-panel-text-secondary, #59636D)", fontSize: ".8rem", lineHeight: 1.6 }}>{help}</Box>
                  </Collapse>
                </Box>
              ) : null}

              {footer ? <Box sx={{ pt: 1, borderTop: "1px solid var(--wp-panel-border, rgba(25,36,47,.08))", color: "var(--wp-panel-text-secondary, #59636D)", textAlign: "center", fontSize: ".86rem" }}>{footer}</Box> : null}
            </Stack>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export function AuthPasswordField({
  label,
  name,
  value,
  onChange,
  autoComplete = "new-password",
  helperText,
  disabled = false,
  required = true,
  inputProps,
  autoFocus = false,
}) {
  const [visible, setVisible] = React.useState(false);

  return (
    <TextField
      label={label}
      name={name}
      type={visible ? "text" : "password"}
      value={value}
      onChange={onChange}
      required={required}
      disabled={disabled}
      fullWidth
      autoFocus={autoFocus}
      autoComplete={autoComplete}
      helperText={helperText}
      inputProps={{
        minLength: 8,
        maxLength: 128,
        autoCapitalize: "none",
        spellCheck: "false",
        ...inputProps,
      }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              type="button"
              aria-label={visible ? "Ocultar contraseña" : "Mostrar contraseña"}
              onClick={() => setVisible((current) => !current)}
              edge="end"
            >
              {visible ? <VisibilityOffRoundedIcon /> : <VisibilityRoundedIcon />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
}
